// Libs
import { useFormContext } from 'react-hook-form';
import cn from 'classnames';

// RDS
import Button from '@ramsey-design-system/button';
import Heading from '@ramsey-design-system/heading';
import Text from '@ramsey-design-system/text';

// Components
import WorkshopStepNavigation from '../WorkshopStepNavigation';

// Hooks
import { STEPS, useWorkshopStepContext } from '../WorkshopStepContext';

// Utils
import { formatWorkshopDateTime } from '../utils/prepare-workshop-payload';

// Types
import { WorkshopFormValues } from '../types';

function SummaryItem({
  label,
  value,
  testId,
  onClick
}: {
  label: string;
  value: string;
  testId: string;
  onClick?: () => void;
}) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    onClick?.();
  };

  return (
    <div className="mb-2 flex items-start justify-between">
      <div className="w-full pr-2 md:pr-4">
        <Heading level="4" className="text-3 mb-0.5">
          {label}
        </Heading>
        <Text className="text-3" data-testid={testId}>
          {value}
        </Text>
      </div>
      {onClick && (
        <Button
          appearance="subtle"
          size="small"
          type="button"
          onClick={handleClick}
          aria-label={`Edit ${label}`}
          data-testid={`${testId}-update-button`}
        >
          Update
        </Button>
      )}
    </div>
  );
}

function WorkshopSummary({ isSubmitting }: { isSubmitting?: boolean }) {
  const { getValues } = useFormContext<WorkshopFormValues>();
  const values = getValues();

  const { helpers, isEditMode, setFocusField } = useWorkshopStepContext();
  const { setStep } = helpers;

  const { formattedDate, formattedTime } = formatWorkshopDateTime(values);

  const dateAndTime = [formattedDate, formattedTime && `at ${formattedTime}`]
    .filter(Boolean)
    .join(' ');

  return (
    <section>
      <Heading level="2" className={cn('text-6 mb-3', { hidden: isEditMode })}>
        Workshop Summary
      </Heading>

      <SummaryItem
        label="Details"
        value={values.name || 'N/A'}
        testId="summary-name"
        onClick={
          isEditMode
            ? () => {
                setFocusField('name');
                setStep(STEPS.DETAILS);
              }
            : undefined
        }
      />

      <SummaryItem
        label="Location"
        value={values.location?.trim() || 'N/A'}
        testId="summary-location"
        onClick={
          isEditMode
            ? () => {
                setFocusField('location');
                setStep(STEPS.DETAILS);
              }
            : undefined
        }
      />

      <SummaryItem
        label="Date"
        value={dateAndTime || 'N/A'}
        testId="summary-date"
        onClick={isEditMode ? () => setStep(STEPS.SCHEDULE) : undefined}
      />

      <SummaryItem
        label="Additional Notes"
        value={values.additionalNotes?.trim() || 'N/A'}
        testId="summary-notes"
        onClick={isEditMode ? () => setStep(STEPS.NOTES) : undefined}
      />

      <WorkshopStepNavigation isSubmitting={isSubmitting} />
    </section>
  );
}

export default WorkshopSummary;
